import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { KEYS, storage } from '@/lib/storage';
import { ArrowLeft, X, Info, Shield, QrCode } from 'lucide-react';

export default function JoinEventPage() {
  const navigate = useNavigate();
  const { status } = useAuth();
  const [sp] = useSearchParams();
  const tokenParam = sp.get('token');
  const codeParam = sp.get('event_id_code');
  const [code, setCode] = useState(codeParam || '');
  const [error, setError] = useState(null);
  const [scanMsg, setScanMsg] = useState(null);

  useEffect(() => {
    if (status === 'loading') return;
    if (!tokenParam) return;
    if (status === 'signed-out') {
      storage.set(KEYS.pendingJoinToken, tokenParam);
      navigate('/sign-in', { replace: true });
      return;
    }
    navigate(`/event-preview?token=${encodeURIComponent(tokenParam)}`, { replace: true });
  }, [status, tokenParam, navigate]);

  function onContinue() {
    setError(null);
    const trimmed = code.trim();
    if (!trimmed) { setError('Enter an Event ID or open your invite link.'); return; }
    if (status === 'signed-out') {
      storage.set(KEYS.pendingJoinCode, trimmed);
      navigate('/sign-in', { replace: true });
      return;
    }
    navigate(`/event-preview?event_id_code=${encodeURIComponent(trimmed)}`);
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="join-event-screen">
      <div className="w-full max-w-md">
        <button onClick={() => navigate(status === 'signed-in' ? '/' : -1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="join-back-btn">
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        <div className="mb-1 flex items-center gap-2"><img src="/allviews-mark.svg" alt="" className="h-5 w-5" /><img src="/allviews-wordmark.svg" alt="ALLVIEWS" className="h-3.5" /></div>

        <h1 className="text-2xl font-extrabold text-[#0B1B46] mt-6 mb-1" data-testid="join-title">Join Event</h1>
        <p className="text-sm text-[#5B6472] mb-4">Enter your Event ID or open your invite link.</p>

        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="join-card">
          <label htmlFor="join-input" className="block text-sm font-semibold text-[#0B1B46] mb-1.5">Event ID</label>
          <div className="relative mb-3">
            <input id="join-input" placeholder="SJWED4827" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              className="w-full h-11 px-4 pr-10 border border-[#E3E8EF] rounded-lg text-sm font-mono tracking-wider uppercase focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white"
              data-testid="join-event-id-input" />
            {code && (
              <button onClick={() => setCode('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5B6472]" data-testid="join-clear-icon">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button onClick={onContinue}
            className="w-full h-11 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="join-continue-btn">Continue</button>
        </div>

        {error && (
          <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434] mb-4" data-testid="join-error">{error}</div>
        )}

        <div className="flex items-center gap-3 my-4" data-testid="join-or-divider">
          <div className="flex-1 h-px bg-[#E3E8EF]" />
          <span className="text-xs font-bold text-[#5B6472] tracking-widest">OR</span>
          <div className="flex-1 h-px bg-[#E3E8EF]" />
        </div>

        <button onClick={() => setScanMsg('Camera scanning requires the mobile app. Please enter the Event ID or open your invite link on this device.')}
          className="w-full h-11 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
          data-testid="join-scan-qr-btn">
          <QrCode className="w-4 h-4" /> Scan QR Code
        </button>

        {scanMsg && (
          <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-lg p-3 text-sm text-[#5B6472] mt-3" data-testid="join-scan-msg">{scanMsg}</div>
        )}

        <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-lg p-3 mt-4 flex items-start gap-2" data-testid="join-info-banner">
          <Info className="w-4 h-4 text-[#146BFF] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#5B6472]">If you have an invite link, opening it will automatically populate the Event ID.</p>
        </div>

        {status === 'signed-out' && (
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mt-4" data-testid="join-signin-callout">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#146BFF] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-[#0B1B46]">To join an event, you'll need to sign in or create an account.</p>
                <p className="text-sm text-[#5B6472] mt-1">This helps keep your photos and memories secure.</p>
              </div>
            </div>
            <button onClick={() => navigate('/sign-in')}
              className="w-full h-11 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg mt-3 transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
              data-testid="join-signin-btn">Sign In or Create Account</button>
          </div>
        )}
      </div>
    </div>
  );
}
